(function () {
  'use strict';

  // Guard: only run on this page
  if (!document.querySelector('#jins-ghost-in-the-shell')) return;

  // ============================================================
  // LOADER
  // Waits for: (1) loader-target assets, (2) 3D viewer ready
  // ============================================================
  class InitLoader {
    constructor() {
      this.isAssetLoaded = false;
      this.isGlassesLoaded = false;
      this.isCompleted = false;
      // Skip 3D viewer on mobile — static .sp image is used instead
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile) {
        this.isGlassesLoaded = true;
      } else {
        this.bindGlassesReady();
        this.prepareGlassesViewer();
      }
      this.waitAssets();
      this.setFallbackTimer();
    }
    bindGlassesReady() {
      window.addEventListener('glassesViewerReady', () => {
        this.isGlassesLoaded = true;
        this.tryComplete();
      }, { once: true });
    }
    prepareGlassesViewer() {
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('prepareGlassesViewer'));
      });
    }
    setFallbackTimer() {
      // If 3D viewer never fires ready, unblock loader after 8s
      window.setTimeout(() => {
        this.isGlassesLoaded = true;
        this.tryComplete();
      }, 8000);
    }
    waitAssets() {
      const targets = Array.from(document.querySelectorAll('.js-loader-target'));
      if (!targets.length) {
        this.isAssetLoaded = true;
        this.tryComplete();
        return;
      }
      let loadedCount = 0;
      const loaded = () => {
        loadedCount++;
        if (loadedCount >= targets.length) {
          this.isAssetLoaded = true;
          this.tryComplete();
        }
      };
      targets.forEach((target) => {
        if (target instanceof HTMLImageElement) {
          if (target.complete) { loaded(); }
          else {
            target.addEventListener('load', loaded, { once: true });
            target.addEventListener('error', loaded, { once: true });
          }
          return;
        }
        if (target instanceof HTMLVideoElement) {
          if (target.readyState >= 2) { loaded(); }
          else {
            target.addEventListener('loadeddata', loaded, { once: true });
            target.addEventListener('error', loaded, { once: true });
          }
          return;
        }
        loaded();
      });
    }
    tryComplete() {
      if (this.isCompleted) return;
      if (!this.isAssetLoaded) return;
      if (!this.isGlassesLoaded) return;
      this.isCompleted = true;
      this.complete();
    }
    complete() {
      const loader = document.querySelector('.loader');
      if (!loader) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          loader.classList.add('is-loaded');
        });
      });
    }
  }

  // ============================================================
  // CURSOR — vanilla rAF lerp (replaces GSAP quickTo)
  // ============================================================
  function initCursor() {
    const cursor = document.getElementById('u-mouse-cursor');
    const follower = document.getElementById('u-mouse-follower');
    if (!cursor || !follower) return;

    // Desktop only
    if (window.matchMedia('(max-width: 767px)').matches) return;

    let mouseX = 0, mouseY = 0;
    let followerX = 0, followerY = 0;
    const LERP = 0.12;

    window.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      cursor.style.left = mouseX + 'px';
      cursor.style.top  = mouseY + 'px';
    });

    function tick() {
      followerX += (mouseX - followerX) * LERP;
      followerY += (mouseY - followerY) * LERP;
      follower.style.left = followerX + 'px';
      follower.style.top  = followerY + 'px';
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    // is-active on hover targets
    document.addEventListener('pointerover', (e) => {
      if (e.target && e.target.closest('.js-mouse-hover, a')) {
        cursor.classList.add('is-active');
      }
    });
    document.addEventListener('pointerout', (e) => {
      if (e.target && e.target.closest('.js-mouse-hover, a')) {
        cursor.classList.remove('is-active');
      }
    });
  }

  // ============================================================
  // INVIEW — IntersectionObserver reveal (.inview → .is-inview)
  // ============================================================
  function initInview() {
    const els = document.querySelectorAll('#jins-ghost-in-the-shell .inview');
    if (!els.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    els.forEach((el) => observer.observe(el));
  }

  // ============================================================
  // LAZY VIDEO LOADER — IntersectionObserver (500px pre-load)
  // ============================================================
  function initLazyVideos() {
    const videos = document.querySelectorAll('#jins-ghost-in-the-shell .js-lazy-video');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (!entry.isIntersecting) return;
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src || '';
          source.removeAttribute('data-src');
        });
        video.load();
        video.play().catch(() => {});
        observer.unobserve(video);
      });
    }, { rootMargin: '500px 0px', threshold: 0 });
    videos.forEach((video) => observer.observe(video));
  }

  // ============================================================
  // CURRENT TIME TEXT — live clock for .js-content-time
  // ============================================================
  class CurrentTimeText {
    constructor(el) {
      this.el = el;
      this.prevText = '';
      this.months = [
        'JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE',
        'JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'
      ];
      this.update();
      window.setInterval(() => this.update(), 1000);
    }
    zero(num) { return num < 10 ? '0' + num : '' + num; }
    format(date) {
      const month = this.months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      const hh = this.zero(date.getHours());
      const mm = this.zero(date.getMinutes());
      const ss = this.zero(date.getSeconds());
      return `${month} ${day}, ${year}.  ${hh}:${mm}:${ss}`;
    }
    update() {
      const text = this.format(new Date());
      this.el.innerHTML = '';
      for (let i = 0; i < text.length; i++) {
        const char = text.charAt(i);
        const prevChar = this.prevText.charAt(i);
        const isChanged = char !== prevChar;
        if (char === ':') {
          const span = document.createElement('span');
          span.className = 'time-char time-colon';
          span.textContent = ':';
          this.el.appendChild(span);
          continue;
        }
        if (/\d/.test(char)) {
          const outer = document.createElement('span');
          outer.className = 'time-digit';
          const inner = document.createElement('span');
          inner.className = 'time-digit__inner';
          inner.textContent = char;
          if (!isChanged) inner.style.animation = 'none';
          outer.appendChild(inner);
          this.el.appendChild(outer);
          continue;
        }
        const span = document.createElement('span');
        span.className = 'time-char';
        span.innerHTML = char === ' ' ? '&nbsp;' : char;
        this.el.appendChild(span);
      }
      this.prevText = text;
    }
  }

  // ============================================================
  // CYBER NUMBERS — glitch scramble on .js-cyber-numbers
  // ============================================================
  class CyberNumbers {
    constructor(root) {
      this.els = root.querySelectorAll('.num');
      this.chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      this.start();
    }
    getRandomText(length) {
      let text = '';
      for (let i = 0; i < length; i++) {
        text += this.chars.charAt(Math.floor(Math.random() * this.chars.length));
      }
      return text;
    }
    glitch(el) {
      const original = el.dataset.original || el.textContent || '';
      const length = original.length;
      let count = 0;
      const max = 35 + Math.floor(Math.random() * 20);
      el.classList.add('is-glitching');
      const timer = window.setInterval(() => {
        el.textContent = this.getRandomText(length);
        count++;
        if (count >= max) {
          window.clearInterval(timer);
          el.textContent = original;
          el.classList.remove('is-glitching');
        }
      }, 18);
    }
    start() {
      const loop = () => {
        this.els.forEach((el) => {
          if (Math.random() > 0.35) return;
          this.glitch(el);
        });
        const nextDelay = 4000 + Math.floor(Math.random() * 3000);
        window.setTimeout(loop, nextDelay);
      };
      loop();
    }
  }

  // ============================================================
  // OUTLINE GLITCH TEXT — product name glitch effect
  // ============================================================
  class OutlineGlitchText {
    constructor(el) {
      this.el = el;
      this.original = el.innerHTML;
      this.plain = this.original.replace(/<br\s*\/?>/gi, '\n');
      this.chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_/[]{}<>#%&';
      this.isIdle = true;
      this.idleTimer = null;
      this.state = 'idle';
      this.init();
    }
    init() {
      this.el.classList.add('is-outline-idle');
      this.scheduleGlitch();
    }
    randomChar() {
      return this.chars.charAt(Math.floor(Math.random() * this.chars.length));
    }
    scheduleGlitch() {
      const delay = 3000 + Math.random() * 4000;
      this.idleTimer = window.setTimeout(() => this.startGlitch(), delay);
    }
    startGlitch() {
      if (this.state !== 'idle') return;
      this.state = 'glitching';
      this.el.classList.remove('is-outline-idle');
      this.el.classList.add('is-outline-glitch');
      const chars = this.plain.split('');
      let frame = 0;
      const maxFrame = 18;
      const timer = window.setInterval(() => {
        const indices = chars
          .map((c, i) => ({ c, i }))
          .filter(({ c }) => c !== '\n' && c !== ' ')
          .sort(() => Math.random() - 0.5)
          .slice(0, Math.ceil(chars.length * 0.4));
        const result = [...chars];
        for (const { i } of indices) result[i] = this.randomChar();
        this.el.innerHTML = result.join('').replace(/\n/g, '<br>');
        frame++;
        if (frame >= maxFrame) {
          window.clearInterval(timer);
          this.el.innerHTML = this.original;
          this.el.classList.remove('is-outline-glitch');
          this.el.classList.add('is-outline-idle');
          this.state = 'idle';
          this.scheduleGlitch();
        }
      }, 40);
    }
  }

  // ============================================================
  // TERMINAL LINES — typewriter / shuffle-in effect
  // ============================================================
  class TerminalLines {
    constructor(el) {
      this.el = el;
      this.index = 0;
      this.chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-_/[]{}<>#%&';
      this.shiftDuration = 650;
      this.lines = this.el.innerHTML
        .split(/<br\s*\/?>/gi)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      if (this.lines.length === 0) return;
      this.visibleLines = [...this.lines];
      this.el.innerHTML = '';
      this.inner = document.createElement('span');
      this.inner.className = 'terminal-lines__inner';
      this.el.appendChild(this.inner);
      this.render();
      window.setTimeout(() => this.shuffleNextLine(), 800 + Math.random() * 800);
    }
    randomChar() {
      return this.chars.charAt(Math.floor(Math.random() * this.chars.length));
    }
    render(extraLine) {
      const lines = typeof extraLine === 'string'
        ? [...this.visibleLines, extraLine]
        : [...this.visibleLines];
      this.inner.innerHTML = lines
        .map((line, i) => {
          const isCurrent = typeof extraLine === 'string' && i === lines.length - 1;
          return `<span class="terminal-line${isCurrent ? ' is-current' : ''}">${line}</span>`;
        })
        .join('');
    }
    shuffleNextLine() {
      const nextLine = this.lines[this.index];
      const length = nextLine.length;
      let frame = 0;
      const maxFrame = 22 + Math.floor(Math.random() * 12);
      const timer = window.setInterval(() => {
        const progress = frame / maxFrame;
        let output = '';
        for (let i = 0; i < length; i++) {
          const originalChar = nextLine.charAt(i);
          if (originalChar === ' ') { output += ' '; continue; }
          output += (i / length < progress) ? originalChar : this.randomChar();
        }
        this.render(output);
        frame++;
        if (frame > maxFrame) {
          window.clearInterval(timer);
          this.render(nextLine);
          window.setTimeout(() => this.shift(nextLine), 180);
        }
      }, 28);
    }
    shift(nextLine) {
      this.inner.classList.add('is-shifting');
      window.setTimeout(() => {
        this.visibleLines.shift();
        this.visibleLines.push(nextLine);
        this.inner.classList.remove('is-shifting');
        this.render();
        this.index = (this.index + 1) % this.lines.length;
        this.shuffleNextLine();
      }, this.shiftDuration);
    }
  }

  // ============================================================
  // PROMOTION — click muted bg → inject full audio YouTube player
  // ============================================================
  function initPromotion() {
    const movieWrap = document.querySelector('.js-promotion-movie');
    const player    = document.querySelector('.js-player');
    const thumb     = document.querySelector('.js-movie-thumb');
    const playBtn   = document.querySelector('.js-play-btn');
    if (!movieWrap) return;
    movieWrap.addEventListener('click', () => {
      if (!player || !thumb || !playBtn) return;
      if (player.classList.contains('is-active')) return;
      thumb.classList.add('is-hide');
      playBtn.classList.add('is-hide');
      player.classList.add('is-active');
      player.innerHTML = `<iframe
        src="https://www.youtube.com/embed/z7CGDrHeXJU?autoplay=1&playsinline=1&rel=0"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
        frameborder="0">
      </iframe>`;
    });
  }

  // ============================================================
  // SVG LOGO ANIMATION — CSS stroke-dashoffset (replaces GSAP DrawSVG)
  // ============================================================
  function initLogoAnimation() {
    const logo = document.querySelector('.gits-kv__logo');
    if (!logo) return;
    // Trigger CSS animation by adding class after a short delay
    window.setTimeout(() => {
      logo.classList.add('is-animating');
    }, 300);
  }

  // ============================================================
  // INIT — DOMContentLoaded
  // ============================================================
  document.addEventListener('DOMContentLoaded', function () {
    if (!document.querySelector('#jins-ghost-in-the-shell')) return;

    new InitLoader();
    initCursor();
    initInview();
    initLazyVideos();
    initLogoAnimation();

    initPromotion();
    document.querySelectorAll('.js-content-time').forEach((el) => new CurrentTimeText(el));
    document.querySelectorAll('.js-cyber-numbers').forEach((el) => new CyberNumbers(el));
    document.querySelectorAll('.js-outline-glitch').forEach((el) => new OutlineGlitchText(el));
    document.querySelectorAll('.js-terminal-lines').forEach((el) => new TerminalLines(el));
  });

})();
